/**
 * API Client
 * Handles all communication with the Cloudflare Workers API
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.myleadsyncer.com';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  /** HTTP status, set on non-2xx responses */
  status?: number;
}

// ============================================
// Plan (402) errors
// ============================================

/** Error codes the worker returns with HTTP 402 when a plan blocks an action. */
export const PLAN_ERROR_CODES = ['plan_limit', 'plan_required', 'subscription_required'] as const;

export function isPlanError(res: { error?: string; status?: number } | null | undefined): boolean {
  if (!res) return false;
  return res.status === 402 || (PLAN_ERROR_CODES as readonly string[]).includes(res.error ?? '');
}

// Pages keep errors as plain strings, so remember which messages came from a
// 402 — <ErrorMessage> uses this to append an "Upgrade under Billing" link.
const planErrorMessages = new Set<string>();

function markPlanErrorMessage(message: string): void {
  planErrorMessages.add(message);
}

export function isPlanErrorMessage(message: string | null | undefined): boolean {
  return !!message && planErrorMessages.has(message);
}

// ============================================
// Clerk session token (wired from inside <ClerkProvider>)
// ============================================

type TokenGetter = (opts?: { skipCache?: boolean }) => Promise<string | null>;

let tokenGetter: TokenGetter | null = null;
let unauthorizedHandler: (() => void) | null = null;

/**
 * Register how apiRequest() obtains a Clerk session token. Called by the
 * AuthProvider (which lives inside <ClerkProvider>) so this module stays a
 * plain, Clerk-agnostic function library.
 */
export function setTokenGetter(getter: TokenGetter | null): void {
  tokenGetter = getter;
}

/** Called when the API still answers 401 after a fresh-token retry. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function getSessionToken(skipCache = false): Promise<string | null> {
  if (!tokenGetter) return null;
  try {
    return await tokenGetter(skipCache ? { skipCache: true } : undefined);
  } catch (err) {
    // Clerk throws when offline or not yet loaded; the request goes out
    // unauthenticated and the API's 401 drives the normal handling.
    console.warn('Could not get session token:', err);
    return null;
  }
}

// ============================================
// Workspace (agency → client workspace) selection
// ============================================

/** Per-tab: sessionStorage, so two tabs can sit in different workspaces. */
const WORKSPACE_STORAGE_KEY = 'mls-workspace';

function readStoredWorkspace(): string | null {
  try {
    return window.sessionStorage.getItem(WORKSPACE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

let workspaceId: string | null = readStoredWorkspace();

/** The client workspace this tab is switched into, or null for the user's own account. */
export function getWorkspaceId(): string | null {
  return workspaceId;
}

/** Set (or clear with null) the workspace sent as `X-MLS-Workspace` on every /api request. */
export function setWorkspaceId(id: string | null): void {
  workspaceId = id;
  try {
    if (id) window.sessionStorage.setItem(WORKSPACE_STORAGE_KEY, id);
    else window.sessionStorage.removeItem(WORKSPACE_STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode / blocked) — the in-memory copy still works for this page */
  }
}

interface RequestBehaviour {
  /** Never send X-MLS-Workspace (e.g. MCP consent must act as the signed-in user's own account). */
  noWorkspace?: boolean;
}

async function authHeaders(skipCache: boolean, behaviour: RequestBehaviour): Promise<{ headers: Record<string, string>; sentWorkspace: boolean }> {
  const headers: Record<string, string> = {};
  const token = await getSessionToken(skipCache);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const ws = behaviour.noWorkspace ? null : workspaceId;
  if (ws) headers['X-MLS-Workspace'] = ws;
  return { headers, sentWorkspace: !!ws };
}

/** Shared 401 / workspace_forbidden handling — both navigate away. */
let handlingUnauthorized = false;

function handleUnauthorized(): void {
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;
  setWorkspaceId(null);
  if (unauthorizedHandler) unauthorizedHandler();
  else window.location.assign('/login');
}

function handleWorkspaceForbidden(): void {
  // The stored workspace is no longer ours (removed / access revoked):
  // drop it and reload as the user's own account.
  setWorkspaceId(null);
  window.location.reload();
}

/**
 * Fetch with the Clerk session token. On 401 retries once with a freshly
 * minted token; if that is still 401 the user is signed out.
 */
async function authedFetch(url: string, init: RequestInit, behaviour: RequestBehaviour): Promise<Response> {
  const send = async (skipCache: boolean) => {
    const { headers, sentWorkspace } = await authHeaders(skipCache, behaviour);
    const response = await fetch(url, {
      ...init,
      headers: { ...(init.headers as Record<string, string> || {}), ...headers },
    });
    return { response, sentWorkspace };
  };

  let { response, sentWorkspace } = await send(false);
  if (response.status === 401) {
    ({ response, sentWorkspace } = await send(true));
    if (response.status === 401) handleUnauthorized();
  }

  if (response.status === 403 && sentWorkspace) {
    try {
      const body = await response.clone().json();
      if (body?.error === 'workspace_forbidden') handleWorkspaceForbidden();
    } catch { /* non-JSON 403 */ }
  }

  return response;
}

/**
 * Make an authenticated API request (Clerk bearer token + optional workspace header).
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  behaviour: RequestBehaviour = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await authedFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> || {}),
      },
    }, behaviour);

    let data: { success?: boolean; error?: string; message?: string } & Record<string, unknown>;
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      const message = data.message || 'Request failed';
      if (response.status === 402) markPlanErrorMessage(message);

      return {
        success: false,
        error: data.error || 'request_failed',
        message,
        status: response.status,
        // Clerk's reverification hint, passed through for useReverification().
        ...(data.clerk_error ? { clerk_error: data.clerk_error } : {}),
      };
    }

    return data as unknown as ApiResponse<T>;
  } catch (error) {
    console.error('API request error:', error);
    return {
      success: false,
      error: 'network_error',
      message: 'Network error. Please check your connection.',
    };
  }
}

/**
 * Download a file from an authenticated endpoint and hand it to the browser.
 *
 * apiRequest() always parses JSON, which would corrupt a CSV/JSONL body, so
 * exports go through here instead: same auth + 401-retry behaviour, but the
 * response is read as a blob and saved via a temporary object URL.
 */
export async function downloadFile(endpoint: string, fallbackName: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await authedFetch(url, {}, {});

    if (!response.ok) {
      // Errors still come back as JSON even on a download endpoint.
      let message = 'Download failed';
      try {
        const body = await response.json();
        message = body.message || message;
      } catch { /* non-JSON error body */ }
      return { ok: false, message };
    }

    // Prefer the server's filename; it already carries the resource name + date.
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : fallbackName;

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);

    return { ok: true };
  } catch (error) {
    console.error('Download error:', error);
    return { ok: false, message: 'Network error. Please check your connection.' };
  }
}

// ============================================
// Auth API
// ============================================

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt?: number;
  /** Set while an agency user is switched into a client workspace: the real (agency) user. */
  actor?: { id: string; email: string } | null;
  /** Set while switched into a client workspace. */
  workspace?: { id: string; name: string } | null;
}

export interface AuthConfig {
  publishableKey: string;
}

export const authApi = {
  /** Public: the Clerk publishable key (not baked into the build). */
  async getConfig(): Promise<ApiResponse<AuthConfig>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/config`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, error: data.error || 'request_failed', message: data.message || 'Request failed', status: response.status };
      }
      return data;
    } catch {
      return { success: false, error: 'network_error', message: 'Network error. Please check your connection.' };
    }
  },

  /** The MyLeadSyncer account behind the Clerk session (honours X-MLS-Workspace). */
  async getMe(): Promise<ApiResponse<User>> {
    return apiRequest<User>('/api/users/me');
  },
};

// ============================================
// MCP OAuth consent (AI clients like Claude)
// ============================================

export interface McpAuthorizeRequest {
  clientName: string;
  redirectHost: string;
  scopes: string[];
}

/** Always runs as the signed-in user's own account — never inside a client workspace. */
export const mcpAuthorizeApi = {
  async get(requestId: string): Promise<ApiResponse<McpAuthorizeRequest>> {
    return apiRequest<McpAuthorizeRequest>(`/api/mcp/authorize/${encodeURIComponent(requestId)}`, {}, { noWorkspace: true });
  },

  async decide(requestId: string, approve: boolean): Promise<ApiResponse<{ redirectTo: string }>> {
    return apiRequest<{ redirectTo: string }>(`/api/mcp/authorize/${encodeURIComponent(requestId)}`, {
      method: 'POST',
      body: JSON.stringify({ approve }),
    }, { noWorkspace: true });
  },
};

// ============================================
// Users API
// ============================================

export const usersApi = {
  async getProfile(): Promise<ApiResponse<User>> {
    return apiRequest<User>('/api/users/profile');
  },

  async updateProfile(firstName?: string, lastName?: string): Promise<ApiResponse> {
    return apiRequest('/api/users/profile', {
      method: 'PUT',
      body: JSON.stringify({ firstName, lastName }),
    });
  },

  /**
   * Permanently delete the signed-in account. Passwords live in Clerk now, so
   * the confirmation is the typed phrase instead of the old password field.
   * Never sent with a workspace header — it must hit the user's own account.
   */
  /** Revokes every AI client (MCP) connection; they must be approved again. */
  async disconnectAiClients(): Promise<ApiResponse> {
    return apiRequest('/api/users/me/disconnect-ai-clients', { method: 'POST' }, { noWorkspace: true });
  },

  async deleteAccount(): Promise<ApiResponse> {
    return apiRequest('/api/users/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirm: 'DELETE' }),
    }, { noWorkspace: true });
  },
};

// ============================================
// Webhooks API
// ============================================

export interface Webhook {
  id: string;
  name: string;
  token: string;
  active: boolean;
  fieldMappings: Record<string, string>;
  connectionId?: string | null;
  selectedTagId?: string | null;
  selectedTagName?: string | null;
  webhookSecret?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface WebhookLog {
  id: string;
  payload: Record<string, unknown>;
  matchedData: Record<string, string> | null;
  status: 'success' | 'error';
  errorMessage?: string;
  createdAt: number;
}

export interface DashboardStats {
  stats: {
    totalWebhooks: number;
    activeWebhooks: number;
    totalLeads: number;
    leadsToday: number;
    connections: number;
  };
  webhooks: {
    id: string;
    name: string;
    active: boolean;
    totalLogs: number;
    errorCount: number;
    lastReceived: number | null;
  }[];
  recentActivity: {
    id: string;
    webhookId: string;
    webhookName: string;
    status: string;
    errorMessage: string | null;
    contactInfo: string | null;
    createdAt: number;
  }[];
}

export const webhooksApi = {
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return apiRequest<DashboardStats>('/api/webhooks/dashboard/stats');
  },

  async list(): Promise<ApiResponse<Webhook[]>> {
    return apiRequest<Webhook[]>('/api/webhooks');
  },

  async create(name: string, fieldMappings?: Record<string, string>): Promise<ApiResponse<Webhook>> {
    return apiRequest<Webhook>('/api/webhooks', {
      method: 'POST',
      body: JSON.stringify({ name, fieldMappings }),
    });
  },

  async get(id: string): Promise<ApiResponse<Webhook>> {
    return apiRequest<Webhook>(`/api/webhooks/${id}`);
  },

  async update(id: string, name: string): Promise<ApiResponse> {
    return apiRequest(`/api/webhooks/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/webhooks/${id}`, {
      method: 'DELETE',
    });
  },

  async toggle(id: string): Promise<ApiResponse<{ active: boolean }>> {
    return apiRequest<{ active: boolean }>(`/api/webhooks/${id}/toggle`, {
      method: 'PUT',
    });
  },

  async updateMappings(
    id: string,
    fieldMappings: Record<string, string>,
    connectionId?: string | null,
    selectedTagId?: string | null,
    selectedTagName?: string | null,
    webhookSecret?: string | null
  ): Promise<ApiResponse> {
    const payload: Record<string, unknown> = { fieldMappings, connectionId, selectedTagId, selectedTagName };
    if (webhookSecret !== undefined) {
      payload.webhookSecret = webhookSecret;
    }
    return apiRequest(`/api/webhooks/${id}/mappings`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  async getLogs(id: string, limit = 50, offset = 0): Promise<ApiResponse<WebhookLog[]>> {
    return apiRequest<WebhookLog[]>(`/api/webhooks/${id}/logs?limit=${limit}&offset=${offset}`);
  },

  generateUrl(token: string): string {
    return `${API_BASE_URL}/webhook/${token}`;
  },
};

// ============================================
// OAuth API
// ============================================

export interface OAuthConnection {
  id: string;
  workspaceId?: string;
  workspaceName?: string;
  workspaceUrl?: string;
  teamId?: string;
  teamName?: string;
  connectionName?: string;
  selectedTag?: string;
  expiresAt: number;
  createdAt: number;
}

export interface CFWorkspace {
  id: number;
  name: string;
  subdomain: string;
  teamId: number;
  teamName: string;
}

export interface CFTag {
  id: string;
  name: string;
}

export const oauthApi = {
  async getConnections(): Promise<ApiResponse<OAuthConnection[]>> {
    return apiRequest<OAuthConnection[]>('/api/oauth/connections');
  },

  async authorize(): Promise<ApiResponse<{ authUrl: string; state: string }>> {
    const redirectUri = `${window.location.origin}/auth/callback`;
    return apiRequest<{ authUrl: string; state: string }>(
      `/api/oauth/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`
    );
  },

  async callback(code: string, state: string): Promise<ApiResponse<OAuthConnection>> {
    const redirectUri = `${window.location.origin}/auth/callback`;
    return apiRequest<OAuthConnection>('/api/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state, redirectUri }),
    });
  },

  async updateName(id: string, connectionName: string): Promise<ApiResponse> {
    return apiRequest(`/api/oauth/${id}/name`, {
      method: 'PUT',
      body: JSON.stringify({ connectionName }),
    });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/oauth/${id}`, {
      method: 'DELETE',
    });
  },

  async refresh(id: string): Promise<ApiResponse<{ expiresAt: number }>> {
    return apiRequest<{ expiresAt: number }>(`/api/oauth/${id}/refresh`, {
      method: 'POST',
    });
  },

  async getToken(id: string): Promise<ApiResponse<{ accessToken: string; expiresAt: number }>> {
    return apiRequest<{ accessToken: string; expiresAt: number }>(`/api/oauth/${id}/token`);
  },

  async updateTag(id: string, selectedTag: string): Promise<ApiResponse> {
    return apiRequest(`/api/oauth/${id}/tag`, {
      method: 'PUT',
      body: JSON.stringify({ selectedTag }),
    });
  },

  async getTags(connectionId: string): Promise<ApiResponse<{ tags: CFTag[] }>> {
    return apiRequest<{ tags: CFTag[] }>(`/api/oauth/${connectionId}/tags`);
  },

  // ClickFunnels API Token flow
  async fetchWorkspaces(apiToken: string, subdomain: string): Promise<ApiResponse<{ workspaces: CFWorkspace[] }>> {
    return apiRequest<{ workspaces: CFWorkspace[] }>('/api/oauth/cf/workspaces', {
      method: 'POST',
      body: JSON.stringify({ apiToken, subdomain }),
    });
  },

  async fetchTags(apiToken: string, subdomain: string, workspaceId: number): Promise<ApiResponse<{ tags: CFTag[] }>> {
    return apiRequest<{ tags: CFTag[] }>('/api/oauth/cf/tags', {
      method: 'POST',
      body: JSON.stringify({ apiToken, subdomain, workspaceId }),
    });
  },

  async connect(params: {
    apiToken: string;
    workspaceId: number;
    workspaceName: string;
    workspaceSubdomain: string;
    teamId: number;
    teamName: string;
    selectedTag?: string;
    selectedTagId?: string;
  }): Promise<ApiResponse<{ connection: OAuthConnection }>> {
    return apiRequest<{ connection: OAuthConnection }>('/api/oauth/connect', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
};

// ============================================
// Admin API
// ============================================

export interface AdminUser extends User {
  stats?: {
    webhooksCount: number;
    oauthConnectionsCount: number;
  };
}

export interface SystemStats {
  users: number;
  webhooks: {
    total: number;
    active: number;
  };
  oauthConnections: number;
  webhookLogs: {
    total: number;
    last24Hours: number;
  };
}

export interface ContentItem {
  id: string;
  key: string;
  value: string;
  createdAt: number;
  updatedAt: number;
}

export interface SystemSetting {
  key: string;
  value: string | null;
  category: string;
  description: string;
  isSecret: boolean;
  source: 'db' | 'env' | 'none';
}

export const adminApi = {
  async getUsers(limit = 50, offset = 0): Promise<ApiResponse<AdminUser[]>> {
    return apiRequest<AdminUser[]>(`/api/admin/users?limit=${limit}&offset=${offset}`);
  },

  async getUser(id: string): Promise<ApiResponse<AdminUser>> {
    return apiRequest<AdminUser>(`/api/admin/users/${id}`);
  },

  async toggleAdmin(id: string): Promise<ApiResponse<{ isAdmin: boolean }>> {
    return apiRequest<{ isAdmin: boolean }>(`/api/admin/users/${id}/admin`, {
      method: 'PUT',
    });
  },

  async deleteUser(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
  },

  async getContent(): Promise<ApiResponse<ContentItem[]>> {
    return apiRequest<ContentItem[]>('/api/admin/content');
  },

  async updateContent(key: string, value: string): Promise<ApiResponse> {
    return apiRequest(`/api/admin/content/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  async deleteContent(key: string): Promise<ApiResponse> {
    return apiRequest(`/api/admin/content/${key}`, {
      method: 'DELETE',
    });
  },

  async getStats(): Promise<ApiResponse<SystemStats>> {
    return apiRequest<SystemStats>('/api/admin/stats');
  },

  async getSettings(): Promise<ApiResponse<{ settings: Record<string, SystemSetting[]> }>> {
    return apiRequest<{ settings: Record<string, SystemSetting[]> }>('/api/admin/settings');
  },

  async updateSetting(key: string, value: string): Promise<ApiResponse> {
    return apiRequest(`/api/admin/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  async deleteSetting(key: string): Promise<ApiResponse> {
    return apiRequest(`/api/admin/settings/${key}`, {
      method: 'DELETE',
    });
  },
};

// ============================================
// Platform Connections API (Vault)
// ============================================

import type { PlatformConnection, PlatformInfo, ResourceTypeConfig, BackupJob, Snapshot, BackupItem, BackupSchedule, RestoreJob } from '../types/vault';

export const platformConnectionsApi = {
  async list(): Promise<ApiResponse<{ connections: PlatformConnection[] }>> {
    return apiRequest('/api/platform-connections');
  },

  async listPlatforms(): Promise<ApiResponse<{ platforms: PlatformInfo[] }>> {
    return apiRequest('/api/platform-connections/platforms');
  },

  async create(platform: string, params: { oauthTokenId?: string; apiKey?: string; subdomain?: string; displayName?: string }): Promise<ApiResponse<{ connection: PlatformConnection }>> {
    return apiRequest('/api/platform-connections', {
      method: 'POST',
      body: JSON.stringify({ platform, ...params }),
    });
  },

  async get(id: string): Promise<ApiResponse<{ connection: PlatformConnection }>> {
    return apiRequest(`/api/platform-connections/${id}`);
  },

  // ClickFunnels contact tags (workspace-wide, reusable across forms/quizzes/buttons)
  async listTags(id: string): Promise<ApiResponse<{ tags: Array<{ id: number; name: string; color: string }> }>> {
    return apiRequest(`/api/platform-connections/${id}/tags`);
  },

  async createTag(id: string, name: string, color?: string): Promise<ApiResponse<{ tag: { id: number; name: string; color: string } }>> {
    return apiRequest(`/api/platform-connections/${id}/tags`, {
      method: 'POST',
      body: JSON.stringify({ name, color }),
    });
  },

  async validate(id: string): Promise<ApiResponse<{ valid: boolean; workspaceName?: string; error?: string }>> {
    return apiRequest(`/api/platform-connections/${id}/validate`, { method: 'POST' });
  },

  async getResourceTypes(id: string): Promise<ApiResponse<{ resourceTypes: ResourceTypeConfig[] }>> {
    return apiRequest(`/api/platform-connections/${id}/resource-types`);
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/platform-connections/${id}`, { method: 'DELETE' });
  },
};

// ============================================
// Backups API (Vault)
// ============================================

export const backupsApi = {
  async start(connectionId: string, resourceTypes: string[]): Promise<ApiResponse<{ job: BackupJob }>> {
    return apiRequest('/api/backups', {
      method: 'POST',
      body: JSON.stringify({ connectionId, resourceTypes }),
    });
  },

  async list(limit = 20, offset = 0): Promise<ApiResponse<{ jobs: BackupJob[] }>> {
    return apiRequest(`/api/backups?limit=${limit}&offset=${offset}`);
  },

  async get(id: string): Promise<ApiResponse<{ job: BackupJob }>> {
    return apiRequest(`/api/backups/${id}`);
  },

  async cancel(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/backups/${id}/cancel`, { method: 'POST' });
  },

  async listSnapshots(limit = 20, offset = 0): Promise<ApiResponse<{ snapshots: Snapshot[] }>> {
    return apiRequest(`/api/backups/snapshots/list?limit=${limit}&offset=${offset}`);
  },

  async getSnapshot(id: string): Promise<ApiResponse<{ snapshot: Snapshot }>> {
    return apiRequest(`/api/backups/snapshots/${id}`);
  },

  async browseItems(snapshotId: string, resourceType?: string, limit = 50, offset = 0): Promise<ApiResponse<{ items: BackupItem[]; total: number; limit: number; offset: number }>> {
    let url = `/api/backups/snapshots/${snapshotId}/items?limit=${limit}&offset=${offset}`;
    if (resourceType) url += `&resourceType=${encodeURIComponent(resourceType)}`;
    return apiRequest(url);
  },

  async getItemData(snapshotId: string, itemId: string): Promise<ApiResponse<{ item: unknown }>> {
    return apiRequest(`/api/backups/snapshots/${snapshotId}/items/${itemId}/data`);
  },
};

// ============================================
// Schedules API (Vault)
// ============================================

export const schedulesApi = {
  async list(): Promise<ApiResponse<{ schedules: BackupSchedule[] }>> {
    return apiRequest('/api/schedules');
  },

  async create(params: { connectionId: string; name: string; cronExpression: string; resourceTypes: string[] }): Promise<ApiResponse<{ schedule: BackupSchedule }>> {
    return apiRequest('/api/schedules', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  async update(id: string, params: { name?: string; cronExpression?: string; resourceTypes?: string[]; enabled?: boolean }): Promise<ApiResponse> {
    return apiRequest(`/api/schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/schedules/${id}`, { method: 'DELETE' });
  },
};

// ============================================
// Restores API (Vault)
// ============================================

export const restoresApi = {
  async start(params: { snapshotId: string; connectionId: string; resourceTypes?: string[]; dryRun?: boolean }): Promise<ApiResponse<{ job: RestoreJob }>> {
    return apiRequest('/api/restores', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  async list(limit = 20, offset = 0): Promise<ApiResponse<{ jobs: RestoreJob[] }>> {
    return apiRequest(`/api/restores?limit=${limit}&offset=${offset}`);
  },

  async get(id: string): Promise<ApiResponse<{ job: RestoreJob }>> {
    return apiRequest(`/api/restores/${id}`);
  },

  async cancel(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/restores/${id}/cancel`, { method: 'POST' });
  },
};

// ============================================
// Archives API (Vault)
// ============================================

export const archivesApi = {
  async getStatus(snapshotId: string): Promise<ApiResponse<{ snapshotId: string; archived: boolean; b2Key?: string; archivedAt?: number }>> {
    return apiRequest(`/api/archives/snapshots/${snapshotId}`);
  },

  async triggerArchive(snapshotId: string): Promise<ApiResponse> {
    return apiRequest(`/api/archives/snapshots/${snapshotId}/archive`, { method: 'POST' });
  },
};

// ============================================
// Forms API
// ============================================

import type { Form, FormField, FormStyleConfig, FormRedirectConfig, FormSettings, FormSubmission } from '../types/forms';
import type { SubmissionPage, ArchivedSubmission, ExportFormat } from '../types/submissions';

export type { Form, FormField, FormStyleConfig, FormRedirectConfig, FormSettings, FormSubmission };

export const formsApi = {
  async list(): Promise<ApiResponse<{ forms: Form[] }>> {
    return apiRequest('/api/forms');
  },

  async create(name: string, connectionId: string): Promise<ApiResponse<{ form: Form }>> {
    return apiRequest('/api/forms', {
      method: 'POST',
      body: JSON.stringify({ name, connectionId }),
    });
  },

  async get(id: string): Promise<ApiResponse<{ form: Form }>> {
    return apiRequest(`/api/forms/${id}`);
  },

  async update(id: string, params: Partial<{
    name: string;
    fields: FormField[];
    styleConfig: FormStyleConfig;
    redirectRules: FormRedirectConfig;
    settings: FormSettings;
    status: string;
  }>): Promise<ApiResponse<{ form: Form }>> {
    return apiRequest(`/api/forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/forms/${id}`, { method: 'DELETE' });
  },

  async getSubmissions(id: string, limit = 50, offset = 0, full = false): Promise<ApiResponse<SubmissionPage<FormSubmission>>> {
    return apiRequest(`/api/forms/${id}/submissions?limit=${limit}&offset=${offset}${full ? '&full=1' : ''}`);
  },

  /** Full archived payload (every field value) for one submission. */
  async getSubmission(id: string, submissionId: string): Promise<ApiResponse<{ submission: ArchivedSubmission }>> {
    return apiRequest(`/api/forms/${id}/submissions/${submissionId}`);
  },

  /** Streams the whole archive as a download. */
  async exportSubmissions(id: string, format: ExportFormat = 'csv', range?: { from?: string; to?: string }) {
    const params = new URLSearchParams({ format });
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    return downloadFile(`/api/forms/${id}/submissions/export?${params}`, `form-submissions.${format}`);
  },

  async getTags(id: string): Promise<ApiResponse<{ tags: Array<{ id: number; name: string; color: string }> }>> {
    return apiRequest(`/api/forms/${id}/tags`);
  },

  getFormUrl(slug: string): string {
    return `${API_BASE_URL}/f/${slug}`;
  },

  getEmbedCode(slug: string): string {
    return `<iframe src="${API_BASE_URL}/f/${slug}" width="100%" height="400" frameborder="0" style="border:none;width:100%;"></iframe>\n<script>(function(){window.addEventListener("message",function(e){if(e.origin!=="${API_BASE_URL}"||!e.data)return;if(e.data.type==="mls-redirect"&&e.data.url){window.location.href=e.data.url;return;}if(e.data.type==="mls-height"&&e.data.height){var f=document.getElementsByTagName("iframe");for(var i=0;i<f.length;i++){if(f[i].contentWindow===e.source){f[i].style.height=e.data.height+"px";}}}});})();</script>`;
  },

  getWidgetCode(slug: string): string {
    return `<script src="${API_BASE_URL}/f/${slug}/widget.js"></script>`;
  },
};

// ============================================
// Quizzes API
// ============================================

import type { Quiz, QuizQuestion, QuizResult, QuizLeadCapture, QuizStyleConfig, QuizSettings, QuizSubmission, VideoConfig, VideoQuizStats } from '../types/quizzes';

/** POST /api/quizzes/:id/video/upload response (video or poster image). */
export interface VideoUploadResult {
  mediaKey: string;
  /** Present for poster uploads on some server versions; falls back to mediaKey. */
  posterKey?: string;
  url: string;
  size: number;
}

/**
 * Upload a raw file body with progress reporting. fetch() has no upload
 * progress, so this uses XMLHttpRequest with the same Clerk token, workspace
 * header, 401 retry and 402 plan-error marking as apiRequest().
 */
export function uploadWithProgress<T>(
  endpoint: string,
  file: Blob,
  opts: { contentType?: string; onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const send = (headers: Record<string, string>) =>
    new Promise<{ status: number; body: { success?: boolean; error?: string; message?: string; data?: unknown } & Record<string, unknown> }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
      xhr.setRequestHeader('Content-Type', opts.contentType || file.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && opts.onProgress) opts.onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        let body = {};
        try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON */ }
        resolve({ status: xhr.status, body });
      };
      xhr.onerror = () => reject(new Error('network_error'));
      xhr.onabort = () => reject(new Error('aborted'));
      if (opts.signal) {
        if (opts.signal.aborted) { reject(new Error('aborted')); return; }
        opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.send(file);
    });

  return (async () => {
    try {
      let { headers } = await authHeaders(false, {});
      let res = await send(headers);
      if (res.status === 401) {
        opts.onProgress?.(0);
        ({ headers } = await authHeaders(true, {}));
        res = await send(headers);
        if (res.status === 401) handleUnauthorized();
      }
      if (res.status < 200 || res.status >= 300) {
        const message = (res.body.message as string) || 'Upload failed';
        if (res.status === 402) markPlanErrorMessage(message);
        return { success: false, error: (res.body.error as string) || 'request_failed', message, status: res.status };
      }
      // Accept both `{ success, data: {...} }` and a bare object.
      const data = (res.body.data ?? res.body) as T;
      return { success: true, data };
    } catch (err) {
      if (err instanceof Error && err.message === 'aborted') {
        return { success: false, error: 'aborted', message: 'Upload cancelled' };
      }
      return { success: false, error: 'network_error', message: 'Network error. Please check your connection.' };
    }
  })();
}

export type { Quiz, QuizQuestion, QuizResult, QuizLeadCapture, QuizStyleConfig, QuizSettings, QuizSubmission };

export const quizzesApi = {
  async list(): Promise<ApiResponse<{ quizzes: Quiz[] }>> {
    return apiRequest('/api/quizzes');
  },

  async create(name: string, connectionId: string, quizType: string, video?: VideoConfig): Promise<ApiResponse<{ quiz: Quiz }>> {
    return apiRequest('/api/quizzes', { method: 'POST', body: JSON.stringify({ name, connectionId, quizType, ...(video ? { video } : {}) }) });
  },

  async get(id: string): Promise<ApiResponse<{ quiz: Quiz }>> {
    return apiRequest(`/api/quizzes/${id}`);
  },

  async update(id: string, params: Partial<{
    name: string; quizType: string; questions: QuizQuestion[]; results: QuizResult[];
    leadCapture: QuizLeadCapture; styleConfig: QuizStyleConfig; settings: QuizSettings; status: string;
    video: VideoConfig | null;
  }>): Promise<ApiResponse<{ quiz: Quiz }>> {
    return apiRequest(`/api/quizzes/${id}`, { method: 'PUT', body: JSON.stringify(params) });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/quizzes/${id}`, { method: 'DELETE' });
  },

  async getSubmissions(id: string, limit = 50, offset = 0, full = false): Promise<ApiResponse<SubmissionPage<QuizSubmission>>> {
    return apiRequest(`/api/quizzes/${id}/submissions?limit=${limit}&offset=${offset}${full ? '&full=1' : ''}`);
  },

  /** Full archived payload (every answer) for one submission. */
  async getSubmission(id: string, submissionId: string): Promise<ApiResponse<{ submission: ArchivedSubmission }>> {
    return apiRequest(`/api/quizzes/${id}/submissions/${submissionId}`);
  },

  /** Streams the whole archive as a download. */
  async exportSubmissions(id: string, format: ExportFormat = 'csv', range?: { from?: string; to?: string }) {
    const params = new URLSearchParams({ format });
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    return downloadFile(`/api/quizzes/${id}/submissions/export?${params}`, `quiz-submissions.${format}`);
  },

  async getTags(id: string): Promise<ApiResponse<{ tags: Array<{ id: number; name: string; color: string }> }>> {
    return apiRequest(`/api/quizzes/${id}/tags`);
  },

  getQuizUrl(slug: string): string {
    return `${API_BASE_URL}/q/${slug}`;
  },

  getEmbedCode(slug: string): string {
    return `<iframe src="${API_BASE_URL}/q/${slug}" width="100%" height="400" frameborder="0" style="border:none;width:100%;"></iframe>\n<script>(function(){window.addEventListener("message",function(e){if(e.origin!=="${API_BASE_URL}"||!e.data)return;if(e.data.type==="mls-redirect"&&e.data.url){window.location.href=e.data.url;return;}if(e.data.type==="mls-height"&&e.data.height){var f=document.getElementsByTagName("iframe");for(var i=0;i<f.length;i++){if(f[i].contentWindow===e.source){f[i].style.height=e.data.height+"px";}}}if(e.data.type==="mls-scroll"){var g=document.getElementsByTagName("iframe");for(var j=0;j<g.length;j++){if(g[j].contentWindow===e.source){try{var r=g[j].getBoundingClientRect();if(r.top<0){g[j].scrollIntoView({behavior:"smooth",block:"start"});}}catch(_){}}}}});})();</script>`;
  },

  getWidgetCode(slug: string): string {
    return `<script src="${API_BASE_URL}/q/${slug}/widget.js"></script>`;
  },

  // ---------------- Video quizzes ----------------

  /** Upload a video (mp4/webm) or poster image (png/jpeg/webp) as a raw body. */
  uploadVideoMedia(id: string, file: File, opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}) {
    return uploadWithProgress<VideoUploadResult>(`/api/quizzes/${id}/video/upload`, file, opts);
  },

  async getVideoStats(id: string, days = 30): Promise<ApiResponse<VideoQuizStats>> {
    return apiRequest(`/api/quizzes/${id}/video/stats?days=${days}`);
  },

  async retryVideoSession(id: string, sessionId: string): Promise<ApiResponse> {
    return apiRequest(`/api/quizzes/${id}/video/sessions/${encodeURIComponent(sessionId)}/retry`, { method: 'POST' });
  },

  async deleteVideoSession(id: string, sessionId: string): Promise<ApiResponse> {
    return apiRequest(`/api/quizzes/${id}/video/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  },

  /** Same shape as the public `/v/{slug}/config`, but works for drafts (owner only). */
  async getVideoPreviewConfig(id: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiRequest(`/api/quizzes/${id}/video/preview-config`);
  },

  /** Public URL the player streams an uploaded media key from. */
  getVideoMediaUrl(mediaKey: string): string {
    return `${API_BASE_URL}/v/media/${mediaKey.split('/').map(encodeURIComponent).join('/')}`;
  },

  getVideoPlayerScriptUrl(): string {
    return `${API_BASE_URL}/v/player.js`;
  },

  /** Script embed for a video quiz, with ClickFunnels merge fields prefilled. */
  /** Bare page that plays a published video quiz — for trying it (e.g. on a phone) before embedding. */
  getVideoPreviewUrl(slug: string): string {
    return `${API_BASE_URL}/v/${encodeURIComponent(slug)}/preview`;
  },

  getVideoEmbedCode(slug: string): string {
    return `<script src="${API_BASE_URL}/v/player.js" data-quiz="${slug}"\n        data-first-name="{{contact.first_name}}" data-email="{{contact.email}}" async></script>`;
  },
};

// ============================================
// Buttons API
// ============================================

import type { ButtonConfig, ButtonAction, ButtonStyleConfig, ButtonClick } from '../types/buttons';

export type { ButtonConfig, ButtonAction, ButtonStyleConfig, ButtonClick };

export const buttonsApi = {
  async list(): Promise<ApiResponse<{ configs: ButtonConfig[] }>> {
    return apiRequest('/api/buttons');
  },

  async create(name: string, connectionId: string): Promise<ApiResponse<{ config: ButtonConfig }>> {
    return apiRequest('/api/buttons', {
      method: 'POST',
      body: JSON.stringify({ name, connectionId }),
    });
  },

  async get(id: string): Promise<ApiResponse<{ config: ButtonConfig }>> {
    return apiRequest(`/api/buttons/${id}`);
  },

  async update(id: string, params: Partial<{
    name: string;
    buttonActions: ButtonAction[];
    styleConfig: ButtonStyleConfig;
    status: string;
  }>): Promise<ApiResponse<{ config: ButtonConfig }>> {
    return apiRequest(`/api/buttons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/buttons/${id}`, { method: 'DELETE' });
  },

  async getTags(id: string): Promise<ApiResponse<{ tags: Array<{ id: number; name: string; color: string }> }>> {
    return apiRequest(`/api/buttons/${id}/tags`);
  },

  async getClicks(id: string): Promise<ApiResponse<{ clicks: ButtonClick[] }>> {
    return apiRequest(`/api/buttons/${id}/clicks`);
  },

  getScriptCode(slug: string): string {
    return `<script src="${API_BASE_URL}/b/${slug}/script.js"></script>`;
  },
};

// ============================================
// Popups API
// ============================================

import type { Popup, PopupField, PopupStyleConfig, PopupSettings, PopupSubmission, PopupEmbed } from '../types/popups';

export type { Popup, PopupField, PopupStyleConfig, PopupSettings, PopupSubmission, PopupEmbed };

export const popupsApi = {
  async list(): Promise<ApiResponse<{ popups: Popup[] }>> {
    return apiRequest('/api/popups');
  },

  async create(name: string, connectionId: string): Promise<ApiResponse<{ popup: Popup }>> {
    return apiRequest('/api/popups', {
      method: 'POST',
      body: JSON.stringify({ name, connectionId }),
    });
  },

  async get(id: string): Promise<ApiResponse<{ popup: Popup }>> {
    return apiRequest(`/api/popups/${id}`);
  },

  async update(id: string, params: Partial<{
    name: string;
    status: string;
    fields: PopupField[];
    styleConfig: PopupStyleConfig;
    settings: PopupSettings;
  }>): Promise<ApiResponse<{ popup: Popup }>> {
    return apiRequest(`/api/popups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  async delete(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/popups/${id}`, { method: 'DELETE' });
  },

  async getTags(id: string): Promise<ApiResponse<{ tags: Array<{ id: number; name: string; color: string }> }>> {
    return apiRequest(`/api/popups/${id}/tags`);
  },

  async getSubmissions(id: string): Promise<ApiResponse<{ submissions: PopupSubmission[] }>> {
    return apiRequest(`/api/popups/${id}/submissions`);
  },

  async getEmbed(id: string): Promise<ApiResponse<PopupEmbed>> {
    return apiRequest(`/api/popups/${id}/embed`);
  },

  getScriptCode(slug: string): string {
    return `<script src="${API_BASE_URL}/p/${slug}/widget.js" defer></script>`;
  },

  getPopupUrl(slug: string): string {
    return `${API_BASE_URL}/p/${slug}`;
  },
};

// ============================================
// Teams API
// ============================================

import type {
  Team, TeamDetail, TeamMember, TeamInvite, AcceptedInvite,
  ApiKey, CreatedApiKey, NotificationSettings, NotificationTestResult,
} from '../types/teams';

export type {
  Team, TeamDetail, TeamMember, TeamInvite, AcceptedInvite,
  ApiKey, CreatedApiKey, NotificationSettings, NotificationTestResult,
};

export const teamsApi = {
  async list(): Promise<ApiResponse<{ teams: Team[] }>> {
    return apiRequest('/api/teams');
  },

  async create(name: string): Promise<ApiResponse<{ team: Team }>> {
    return apiRequest('/api/teams', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async get(id: string): Promise<ApiResponse<{ team: TeamDetail; members: TeamMember[] }>> {
    return apiRequest(`/api/teams/${id}`);
  },

  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/api/teams/${id}`, { method: 'DELETE' });
  },

  async invite(id: string, email: string, role?: string): Promise<ApiResponse<{ invite: TeamInvite }>> {
    return apiRequest(`/api/teams/${id}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  },

  async listInvites(id: string): Promise<ApiResponse<{ invites: TeamInvite[] }>> {
    return apiRequest(`/api/teams/${id}/invites`);
  },

  async revokeInvite(id: string, inviteId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/api/teams/${id}/invites/${inviteId}`, { method: 'DELETE' });
  },

  async acceptInvite(token: string): Promise<ApiResponse<{ team: AcceptedInvite }>> {
    return apiRequest('/api/teams/invites/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  async removeMember(id: string, userId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/api/teams/${id}/members/${userId}`, { method: 'DELETE' });
  },

  async shareConnection(teamId: string, connectionId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/api/teams/${teamId}/connections/${connectionId}`, { method: 'POST' });
  },

  async unshareConnection(teamId: string, connectionId: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/api/teams/${teamId}/connections/${connectionId}`, { method: 'DELETE' });
  },
};

// ============================================
// API Keys API
// ============================================

export const apiKeysApi = {
  async list(): Promise<ApiResponse<{ apiKeys: ApiKey[] }>> {
    return apiRequest('/api/api-keys');
  },

  async create(name: string, scopes?: string[]): Promise<ApiResponse<{ apiKey: CreatedApiKey }>> {
    return apiRequest('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    });
  },

  async revoke(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiRequest(`/api/api-keys/${id}`, { method: 'DELETE' });
  },
};

// ============================================
// Notifications API (Vault)
// ============================================

export const notificationsApi = {
  async get(): Promise<ApiResponse<{ settings: NotificationSettings }>> {
    return apiRequest('/api/notifications');
  },

  async update(params: {
    webhookUrl?: string;
    emailEnabled?: boolean;
    events?: string[];
  }): Promise<ApiResponse<{ settings: NotificationSettings }>> {
    return apiRequest('/api/notifications', {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  async test(): Promise<ApiResponse<NotificationTestResult>> {
    return apiRequest('/api/notifications/test', { method: 'POST' });
  },
};

// ============================================
// Billing API
// ============================================

export type PlanId = 'free' | 'starter' | 'pro' | 'agency';

export interface PlanLimits {
  connections: number | null;
  forms: number | null;
  quizzes?: number | null;
  buttons?: number | null;
  popups?: number | null;
  webhooks?: number | null;
  teamMembers: number | null;
  workspaces: number | null;
  scheduledBackups: boolean;
  restore: boolean;
  apiAccess: boolean;
  whopApp: boolean;
}

export interface PlanUsage {
  connections: number;
  forms: number;
  quizzes: number;
  buttons: number;
  popups: number;
  webhooks: number;
}

export interface BillingSubscription {
  plan: PlanId;
  status: string;
  renewsAt: number | null;
  cancelAtPeriodEnd: boolean;
  manageUrl: string | null;
}

export interface PlanInfo {
  id: PlanId;
  name: string;
  priceMonthly: number;
  tagline?: string;
  features: string[];
}

export interface WhopLink {
  accountId: string;
  title: string;
}

export interface BillingInfo {
  plan: PlanId;
  source: string;
  trialEndsAt: number | null;
  trialDaysLeft: number | null;
  isWorkspace: boolean;
  /** True until a new account claims its 14-day trial through Whop. */
  trialAvailable?: boolean;
  canManageBilling: boolean;
  limits: PlanLimits;
  usage: PlanUsage;
  subscription: BillingSubscription | null;
  plans: PlanInfo[];
  whopLinks: WhopLink[];
}

export const billingApi = {
  async get(): Promise<ApiResponse<BillingInfo>> {
    return apiRequest<BillingInfo>('/api/billing');
  },

  async checkout(plan: PlanId): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  },

  async startTrial(): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>('/api/billing/trial', { method: 'POST' });
  },

  async sync(): Promise<ApiResponse<BillingInfo>> {
    return apiRequest<BillingInfo>('/api/billing/sync', { method: 'POST' });
  },

  async createWhopLinkCode(): Promise<ApiResponse<{ code: string; expiresAt: number }>> {
    return apiRequest<{ code: string; expiresAt: number }>('/api/billing/whop-link-code', { method: 'POST' });
  },

  async removeWhopLink(accountId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return apiRequest<{ ok: boolean }>(`/api/billing/whop-links/${encodeURIComponent(accountId)}`, { method: 'DELETE' });
  },
};

// ============================================
// Workspaces API (Agency)
// ============================================

export interface Workspace {
  id: string;
  name: string;
  createdAt: number;
}

export interface WorkspacesInfo {
  enabled: boolean;
  limit: number;
  agency: { id: string; email: string };
  current: { id: string; name: string; isWorkspace: boolean } | null;
  workspaces: Workspace[];
}

export const workspacesApi = {
  async list(): Promise<ApiResponse<WorkspacesInfo>> {
    return apiRequest<WorkspacesInfo>('/api/workspaces');
  },

  async create(name: string): Promise<ApiResponse<{ workspace: Workspace }>> {
    return apiRequest<{ workspace: Workspace }>('/api/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async rename(id: string, name: string): Promise<ApiResponse<{ workspace?: Workspace }>> {
    return apiRequest<{ workspace?: Workspace }>(`/api/workspaces/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  },

  async remove(id: string): Promise<ApiResponse> {
    return apiRequest(`/api/workspaces/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};
